import { useState, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogProps,
  JFormik,
  selectUser,
} from '@centerpiece/components';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Switch,
  Tooltip,
  Grid,
} from '@mui/material';
import { ArrowBack, Edit, Save, Close, Delete } from '@mui/icons-material';
import * as Yup from 'yup';
import {
  Action,
  Coordination,
  Attachment,
  Contact,
  Message,
  Response,
  Event,
  Appointment,
} from 'store/api/models';
import moment from 'moment';
import {
  email,
  useCreateAppointmentMutation,
  useDeleteAppointmentMutation,
  useGetTagsQuery,
  useLazyGetAttachmentQuery,
  usePatchAppointmentMutation,
} from 'store/api';
import { useProjectContext } from 'contexts/project.context';
import {
  CreateMessageStatusProps,
  CreateMessageStatus,
} from 'statuses/CreateMessageStatus';
import { downloadFile } from 'utils/downloadFile.util';
import { useEventForm } from 'jformik/useEventForm';
import { useSelector } from 'react-redux';
import { ContactComponent } from 'components/ContactComponent';
import { useAllContacts } from 'utils/contacts.utils';
import { useItemContext } from 'contexts/item.context';
import {
  appointmentInitialValues,
  meetingInitialValues,
  responseInitialValues,
} from 'utils/messaging.utils';

export interface EventActualTabProps {
  onClose?: Function;
  onSubmit?: (values: any) => void | Promise<any>;
  confirmResponse?: Response;
  back?: boolean;
}

export const EventActualTab = ({
  onClose = () => {},
  onSubmit = values => {},
  back,
  confirmResponse,
}: EventActualTabProps) => {
  const {
    id,
    project,
    appointments,
    title,
    coordination,
    event,
    refetchItem,
    afterSave,
    setActiveTab,
  } = useItemContext();
  const appointment = appointments?.[0];
  const author = appointments?.[0]?.contacts?.find(c => c.author);
  const user = useSelector(selectUser);
  const allContacts = useAllContacts();
  const formRef = useRef<any>(null);
  const alertFormRef = useRef<any>(null);
  const [createAppointment] = useCreateAppointmentMutation();
  const [patchAppointment, patchAppointmentResults] =
    usePatchAppointmentMutation();
  const [deleteAppointment, deleteAppointmentResults] =
    useDeleteAppointmentMutation();
  const [getAttachment] = useLazyGetAttachmentQuery({ refetchOnFocus: false });
  const [messageStatus, setMessageStatus] = useState<
    Omit<CreateMessageStatusProps, 'close'>
  >({
    open: false,
    loading: false,
    creatingMessage: false,
    creatingMessageError: false,
    attachments: { pending: 0, created: 0, failed: 0 },
  });
  const [sendNotice, setSendNotice] = useState<boolean>(
    (appointment?.contacts?.length &&
      appointment?.contacts?.filter(c => !c.author)?.length > 0) ||
      !!confirmResponse
      ? true
      : false
  );
  const handleToggle = () => {
    if (formRef.current) {
      setSendNotice(prev => !prev);
    }
  };
  const [alertDetails, setAlertDetails] = useState<AlertDialogProps>();
  const [editing, setEditing] = useState<boolean | undefined>(
    appointment === undefined ? undefined : false
  );
  const [openContact, setOpenContact] = useState<{
    open: boolean;
    id?: number;
    disabled?: boolean;
    callback?: Function;
    close?: Function;
    initialValues?: any;
  }>({ open: false });
  const { events } = useProjectContext();
  const { data: tags } = useGetTagsQuery({});
  const eventForm: any[] = useEventForm({
    events: events.filter(e => !!e.start || !!e.end),
    tags: tags,
    // preview: false,
    // disabled: !editing || false,
    disabled: editing === false,
    editing: editing,
    replied: false, //!!confirmResponse,
    project: project,
    title: title,
    notice: sendNotice,
    noResponseDue: true,
    setOpenContact: setOpenContact,
    contacts: allContacts,
    onDownload: async (file: any) => {
      const attachment: Attachment = await getAttachment(file.id).unwrap();
      await downloadFile(attachment);
    },
    addDateRange: true,
  });

  return (
    <Box>
      <AlertDialog
        open={!!alertDetails}
        title={alertDetails?.title || ''}
        content={alertDetails?.content || ''}
        form={alertDetails?.form}
        onClose={() => {
          alertDetails?.onClose && alertDetails.onClose();
          setAlertDetails(undefined);
        }}
        onConfirm={() => {
          alertDetails?.onConfirm && alertDetails.onConfirm();
          setAlertDetails(undefined);
        }}
      />
      {openContact?.open && (
        <ContactComponent
          enableOrganization={true}
          {...openContact}
          close={() => {
            openContact.close && openContact.close();
            setOpenContact({ open: false });
          }}
        />
      )}
      <Box
        sx={{
          display: openContact?.open ? 'none' : 'block',
          position: 'relative',
        }}
      >
        <CreateMessageStatus
          close={() =>
            setMessageStatus({
              open: false,
              loading: false,
              creatingMessage: false,
              creatingMessageError: false,
              attachments: { pending: 0, created: 0, failed: 0 },
            })
          }
          {...messageStatus}
        />
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap={'nowrap'}
          spacing={0}
          sx={{ width: '100%' }}
        >
          <Grid item>
            {(back || editing) && (
              <Button
                startIcon={editing ? <Close /> : <ArrowBack />}
                color="secondary"
                onClick={() => {
                  if (editing) {
                    setEditing(false);
                    setSendNotice(
                      appointment?.contacts?.length &&
                        appointment?.contacts?.filter(c => !c.author)?.length >
                          0
                        ? true
                        : false
                    );
                  } else {
                    onClose();
                  }
                }}
              >
                {editing ? 'Cancel' : 'Back'}
              </Button>
            )}
          </Grid>

          <Grid item>
            <Grid
              container
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              flexWrap={'nowrap'}
              spacing={1}
            >
              {editing !== false && !confirmResponse && !appointment && (
                <Grid item sx={{ pb: 1 }}>
                  <Tooltip
                    title={
                      'Toggle to add event details to attendee calendars (Outlook, Google, or Apple).'
                    }
                  >
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            color="secondary"
                            checked={sendNotice}
                            onChange={() => handleToggle()}
                          />
                        }
                        label={
                          <Typography variant="button">
                            EMAIL CALENDAR EVENT
                          </Typography>
                        }
                      />
                    </FormGroup>
                  </Tooltip>
                </Grid>
              )}
              {editing !== false && appointment && (
                <Grid item>
                  <Button
                    startIcon={
                      deleteAppointmentResults.isLoading ? (
                        <CircularProgress sx={{ fontSize: '0.5rem' }} />
                      ) : (
                        <Delete fontSize="small" />
                      )
                    }
                    color="secondary"
                    // onClick={() => setEditing(prev => !prev)}
                    onClick={async () => {
                      setAlertDetails({
                        open: true,
                        form: sendNotice && (
                          <JFormik
                            formRef={alertFormRef}
                            initialValues={{}}
                            validationSchema={Yup.object().shape({
                              reason: Yup.string()
                                .nullable()
                                .required(`Please add a reason.`),
                            })}
                            form={[
                              {
                                name: 'reason',
                                itemType: 'textfield',
                                label: 'Please add a cancellation reason',
                                jItemProps: {
                                  multiline: true,
                                  minRows: 3,
                                },
                              },
                            ]}
                          />
                        ),
                        title: sendNotice
                          ? `Send cancelation to participants`
                          : `Delete event`,
                        content: `Are you sure you want to delete this event?`,
                        onConfirm: async () => {
                          if (alertFormRef.current && formRef.current) {
                            const { setFieldValue, submitForm } =
                              formRef.current;
                            setFieldValue('form.body', [
                              {
                                text:
                                  'Cancelation Reason:\n\n' +
                                  alertFormRef.current.values.reason,
                              },
                              // {
                              //   text: '-----------------------------',
                              // },
                              // ...formRef.current.values.form.body,
                            ]);
                            setFieldValue('deleted', true);
                            setFieldValue('canceled', true);
                            await submitForm();
                            setEditing(false);
                            // await deleteAppointment(appointment.id);
                            await refetchItem();
                            await afterSave();
                            onClose();
                          } else if (appointment?.id) {
                            deleteAppointment(appointment?.id);
                          }
                        },
                        onClose: () => setEditing(false),
                      });
                    }}
                  >
                    Delete
                  </Button>
                </Grid>
              )}
              {appointment && (
                <Grid item>
                  <Button
                    startIcon={
                      patchAppointmentResults.isLoading ? (
                        <CircularProgress sx={{ fontSize: '0.5rem' }} />
                      ) : editing ? (
                        <Save fontSize="small" />
                      ) : (
                        <Edit fontSize="small" />
                      )
                    }
                    color="secondary"
                    // onClick={() => setEditing(prev => !prev)}
                    onClick={async () => {
                      if (editing) {
                        if (sendNotice) {
                          setAlertDetails({
                            open: true,
                            title: `Send update to participants`,
                            content: `Are you sure you want to update the meeting notice?`,
                            onConfirm: async () => {
                              setEditing(false);
                              if (formRef.current) {
                                await formRef.current.submitForm();
                                await refetchItem();
                                await afterSave();
                              }
                            },
                            onClose: () => setEditing(false),
                          });
                        } else {
                          setEditing(false);
                          if (formRef.current) {
                            await formRef.current.submitForm();
                            await refetchItem();
                            await afterSave();
                          }
                        }
                      } else {
                        setEditing(true);
                      }
                    }}
                  >
                    {editing ? 'Save' : 'Edit'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Box
          sx={{ paddingTop: back || appointment || confirmResponse ? 1 : 0 }}
        >
          <Box>
            <JFormik
              key={JSON.stringify(formRef.current?.values?.recipients)}
              formRef={formRef}
              initialValues={
                formRef.current?.values
                  ? { ...formRef.current.values }
                  : !!confirmResponse
                  ? meetingInitialValues(
                      confirmResponse,
                      user,
                      `${project?.title}: ${title}`
                    )
                  : appointment
                  ? appointmentInitialValues(appointment)
                  : {
                      from: [
                        {
                          ...user,
                          name: `${user.firstname} ${user.lastname}`,
                        },
                      ],
                      subject: `${project?.title}: ${title}`,
                      type: 'Availability',
                      // form: { body: [{ text: '' }] },
                      form: {
                        body: [
                          {
                            type: 'auto_salutation',
                            text: 'Hey {{first_name}},\n',
                            disabled: true,
                          },
                          {
                            text: '',
                            placeholder: 'Add something here',
                          },
                          // {
                          //   text: '',
                          //   placeholder: 'Add something here',
                          // },
                          {
                            type: 'share_file',
                            text: '',
                          },
                        ],
                        auto_salutation: true,
                      },
                    }
              }
              disabled={editing === false}
              validationSchema={
                sendNotice
                  ? Yup.object()
                      .shape({
                        subject: Yup.string()
                          .nullable()
                          .required(`Please add a subject.`),
                        form: Yup.object().shape({
                          body: Yup.array()
                            .nullable()
                            .min(1, `Please add an email message.`)
                            .required('Please add an email message.'),
                          address: Yup.string()
                            .nullable()
                            .required(`Please add an address.`),
                          city: Yup.string()
                            .nullable()
                            .required(`Please add a city.`),
                          state: Yup.string()
                            .nullable()
                            .required(`Please add a state.`),
                          zipcode: Yup.string()
                            .nullable()
                            .required(`Please add a zipcode.`),
                        }),
                        dates: Yup.string()
                          .nullable()
                          .required(`Please specify the dates of the event`),
                      })
                      .test(
                        'recipients',
                        'At least one field must have a value',
                        function (this: Yup.TestContext, value) {
                          const { path, createError } = this;
                          const { recipients = [], cc = [], bcc = [] } = value;
                          if (
                            !(
                              recipients?.length > 0 ||
                              cc?.length > 0 ||
                              bcc?.length > 0
                            )
                          ) {
                            const keys = ['recipients', 'cc', 'bcc'];
                            const errors = keys
                              .map(key => {
                                return new Yup.ValidationError(
                                  'A valid email address is required in To, Cc, or Bcc',
                                  value[key],
                                  key
                                );
                              })
                              .filter(Boolean);
                            return createError({
                              message: () => errors,
                            });
                          }
                          return false;
                        }
                      )
                  : Yup.object({
                      subject: Yup.string()
                        .nullable()
                        .required(`Please add a subject.`),
                      form: Yup.object().shape({
                        address: Yup.string()
                          .nullable()
                          .required(`Please add an address.`),
                        city: Yup.string()
                          .nullable()
                          .required(`Please add a city.`),
                        state: Yup.string()
                          .nullable()
                          .required(`Please add a state.`),
                        zipcode: Yup.string()
                          .nullable()
                          .required(`Please add a zipcode.`),
                      }),
                      dates: Yup.string()
                        .nullable()
                        .required(`Please specify the dates of the event`),
                    })
              }
              handleSubmit={async (values: any) => {
                const numFiles: number =
                  values?.form?.body?.reduce(
                    (total: number, item: any) =>
                      total +
                        item?.action?.attachments?.filter((a: any) => !a.id)
                          ?.length || 0,
                    0
                  ) || 0;
                setMessageStatus({
                  open: sendNotice,
                  loading: true,
                  creatingMessage: true,
                  creatingMessageError: false,
                  attachments: { pending: numFiles, created: 0, failed: 0 },
                  edit: !!values.id,
                  cancel: values.deleted,
                });
                const msgvalues = {
                  ...values,
                  start_time: values?.start_date,
                  end_time: values?.due_date || values?.start_date, //handle single day
                  location: `${
                    values?.form?.address ? values?.form?.address + ' ' : ''
                  }${values?.form?.city ? values?.form?.city + ' ' : ''}${
                    values?.form?.state ? values?.form?.state + ' ' : ''
                  }${values?.form?.zipcode ? values?.form?.zipcode : ''}`,
                  details: values?.form?.body
                    ?.filter((b: any) => b.text && b.type !== 'auto_salutation')
                    ?.reduce(
                      (acc: string, obj: any) => acc + `${obj.text}`,
                      ''
                    ),
                  form: {
                    ...values.form,
                    body: values?.form?.body
                      ?.map((item: any, index: number) => {
                        if (item.type) {
                          return {
                            ...item,
                            form: {
                              ...item,
                              name: `form.body.${index}.action`,
                            },
                          };
                        } else {
                          return item;
                        }
                      })
                      ?.filter((i: any) => i),
                  },
                  attachments: values?.form?.body?.find((b: any) => b.action)
                    ?.action?.attachments,
                  item: id,
                  type: 'Appointment',
                };
                if (!sendNotice) {
                  // clear out recipients so an email isn't sent
                  msgvalues.recipients = null;
                  msgvalues.cc = null;
                  msgvalues.bcc = null;
                }
                try {
                  let newMessage: Appointment;
                  if (msgvalues.id > 0) {
                    newMessage = await patchAppointment(msgvalues).unwrap();
                  } else {
                    newMessage = await createAppointment(msgvalues).unwrap();
                  }
                  if (newMessage.id > 0) {
                    setMessageStatus(currentStatus => ({
                      ...currentStatus,
                      creatingMessage: false,
                    }));
                    const newAttachments = newMessage?.attachments?.filter(
                      (a: any) => a.url
                    );
                    if (newAttachments?.length > 0) {
                      for (let i = 0; i < newAttachments.length; i++) {
                        const attachment: Attachment = newAttachments[i];
                        const file = msgvalues.attachments?.find(
                          (a: File) => a.name === attachment.name
                        );
                        if (attachment.url && file.file) {
                          const res = await fetch(attachment.url, {
                            method: 'PUT',
                            body: file.file,
                          });

                          if (res.ok) {
                            setMessageStatus(currentStatus => ({
                              ...currentStatus,
                              attachments: {
                                pending: currentStatus.attachments.pending - 1,
                                created: currentStatus.attachments.created + 1,
                                failed: currentStatus.attachments.failed,
                              },
                            }));
                          } else {
                            setMessageStatus(currentStatus => ({
                              ...currentStatus,
                              attachments: {
                                pending: currentStatus.attachments.pending - 1,
                                created: currentStatus.attachments.created,
                                failed: currentStatus.attachments.failed + 1,
                              },
                            }));
                          }
                        } else {
                          setMessageStatus(currentStatus => ({
                            ...currentStatus,
                            attachments: {
                              pending: currentStatus.attachments.pending - 1,
                              created: currentStatus.attachments.created,
                              failed: currentStatus.attachments.failed + 1,
                            },
                          }));
                        }
                      }
                    }
                    setMessageStatus(currentStatus => ({
                      ...currentStatus,
                      loading: false,
                    }));
                    onSubmit(values);
                    await refetchItem();
                    onClose();
                  } else if (values.deleted) {
                    setMessageStatus(currentStatus => ({
                      ...currentStatus,
                      loading: false,
                    }));
                  } else {
                    setMessageStatus(currentStatus => ({
                      ...currentStatus,
                      loading: false,
                      creatingMessage: false,
                      creatingMessageError: true,
                    }));
                  }
                } catch (error) {
                  setMessageStatus(currentStatus => ({
                    ...currentStatus,
                    loading: false,
                    creatingMessage: false,
                    creatingMessageError: true,
                  }));
                  console.error('rejected', error);
                }
              }}
              containerGridProps={{ spacing: 2 }}
              form={eventForm}
            />
          </Box>
          {appointment && appointment?.created_at && appointment?.updated_at && (
            <Typography variant="caption">
              <i>
                Event{' '}
                {moment(appointment.updated_at).isBefore(
                  moment(appointment.created_at).add(2, 'minute')
                )
                  ? sendNotice
                    ? `sent`
                    : `created`
                  : `updated`}{' '}
                {`${moment(appointment?.updated_at).format(
                  'dddd, MMMM Do YYYY, h:mm a'
                )}`}
              </i>
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
